import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {newPalette} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BoxedIcon} from '../../../components/common/boxedIcon';

/*
 * Props.
 */

export interface RuleLibraryCardProps {
  title: ReactNode;
  icon: IconName;
  iconPalette?: PaletteColorsEnum;
  pills?: ReadonlyArray<ReactNode>;

  imageUrl?: string;
  backgroundColor?: string;
}

/*
 * Constants.
 */

export const RULE_LIBRARY_CARD_HEIGHT = 190;
export const RULE_LIBRARY_CARD_WIDTH = 190;
export const RULE_LIBRARY_LARGE_CARD_WIDTH = 600;
export const RULE_LIBRARY_CARD_SHADOW_SIZE = 9;
const PADDING = 20;

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div<{$isLarge: boolean}>`
  height: ${RULE_LIBRARY_CARD_HEIGHT}px;

  padding: ${PADDING}px;

  background-color: ${(p) => p.theme.greys.white};

  border-radius: 16px;
  border: 1px solid ${(p) => p.theme.greys.shade30};
  ${(p) => boxShadow(p.theme, `0px 1px ${RULE_LIBRARY_CARD_SHADOW_SIZE}px ${p.theme.alphas.black20}`)};

  overflow: hidden;

  display: grid;
  ${(p) => getCardStyles(p.$isLarge)}

  :hover {
    background-color: ${(p) => p.theme.greys.shade10};
  }
`;

function getCardStyles(isLarge: boolean) {
  if (isLarge) {
    return css`
      width: ${RULE_LIBRARY_LARGE_CARD_WIDTH}px;
      grid-template-columns: 40px 1fr 50%;
      grid-template-rows: 40px 1fr;
      grid-template-areas:
        'icon     pill    image'
        'content  content image';

      grid-column-gap: 20px;
    `;
  }

  return css`
    width: ${RULE_LIBRARY_CARD_WIDTH}px;
    grid-template-columns: 40px 1fr;
    grid-template-rows: 40px 1fr;
    grid-template-areas:
      'icon     pill   '
      'content  content';
  `;
}

const GridHelper = styled.div<{
  $area: 'icon' | 'pill' | 'image' | 'content' | 'buttons';
  $width?: number;
  $flex?: boolean;
  $align?: 'left' | 'right';
}>`
  ${(p) => (p.$flex ? 'display: flex;' : '')}
  position: relative;
  grid-area: ${(p) => p.$area};
  ${(p) => (p.$width ? `width: ${p.$width}px;` : '')}
`;

const StyledTitle = styled.h2`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.large};
  margin-top: 12px;
  margin-bottom: 5px;
`;

const StyledContent = styled.p`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};
  line-height: 20px;
  color: ${(p) => p.theme.greys.shade70};

  margin-top: 0px;
  margin-bottom: 20px;

  a {
    text-decoration: underline;
    color: inherit;
  }
`;

const ImageWrapper = styled.div<{$backgroundColor: string}>`
  position: absolute;

  top: -${PADDING}px;
  height: ${RULE_LIBRARY_CARD_HEIGHT}px;

  left: 0;
  width: ${RULE_LIBRARY_LARGE_CARD_WIDTH / 2}px;

  display: flex;
  align-items: flex-end;
  justify-content: flex-end;

  background-color: ${(p) => p.$backgroundColor};

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const PillWrapper = styled.div`
  display: flex;
  grid-area: pill;
  height: fit-content;
  align-items: flex-end;
  flex-direction: column;
  > * {
    margin-bottom: 8px;
  }
`;

/*
 * Component.
 */

export const RuleLibraryCard: FC<RuleLibraryCardProps> = ({
  icon,
  iconPalette = PaletteColorsEnum.BLUE,
  title,
  pills,
  children,
  imageUrl,
  backgroundColor = newPalette.blue.tint,
}) => {
  const theme = useUserTheme();
  const isLargeCard = Boolean(imageUrl);

  return (
    <StyledWrapperDiv $isLarge={isLargeCard}>
      <GridHelper $area="icon">
        <BoxedIcon
          name={icon}
          backgroundColor={theme.palette[iconPalette].shade20}
          color={theme.palette[iconPalette].shade40}
        />
      </GridHelper>
      <GridHelper
        $area="content"
        $width={(isLargeCard ? RULE_LIBRARY_LARGE_CARD_WIDTH / 2 : RULE_LIBRARY_CARD_WIDTH) - 2 * PADDING}
      >
        <StyledTitle>{title}</StyledTitle>
        <StyledContent>{children}</StyledContent>
      </GridHelper>
      <PillWrapper>{pills}</PillWrapper>
      {imageUrl && (
        <GridHelper $area="image">
          <ImageWrapper $backgroundColor={backgroundColor}>
            <img src={imageUrl} alt="" />
          </ImageWrapper>
        </GridHelper>
      )}
    </StyledWrapperDiv>
  );
};
